/**
 * Promotion Form Modal
 * Create or edit promotion codes
 */

import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';

interface Promotion {
  id: number;
  code: string;
  name: string;
  description: string | null;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  appliesTo: 'setup' | 'recurring' | 'both';
  minOrderValue: number;
  maxDiscountAmount: number | null;
  appliesToAllProducts: number;
  maxUses: number | null;
  maxUsesPerClient: number;
  requireExistingClient: number;
  newClientsOnly: number;
  recurringDiscount: number;
  recurringMonths: number | null;
  startsAt: string | null;
  expiresAt: string | null;
  isActive: number;
}

interface PromotionFormModalProps {
  promotion: Promotion | null;
  onClose: () => void;
}

export const PromotionFormModal: React.FC<PromotionFormModalProps> = ({
  promotion,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discountType: 'percentage' as 'percentage' | 'fixed',
    discountValue: 0,
    appliesTo: 'both' as 'setup' | 'recurring' | 'both',
    minOrderValue: 0,
    maxDiscountAmount: '',
    appliesToAllProducts: true,
    billingCycles: [] as string[],
    maxUses: '',
    maxUsesPerClient: 1,
    requireExistingClient: false,
    newClientsOnly: false,
    recurringDiscount: false,
    recurringMonths: '',
    startsAt: '',
    expiresAt: '',
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (promotion) {
      setFormData({
        code: promotion.code,
        name: promotion.name,
        description: promotion.description || '',
        discountType: promotion.discountType,
        discountValue: promotion.discountValue,
        appliesTo: promotion.appliesTo,
        minOrderValue: promotion.minOrderValue,
        maxDiscountAmount: promotion.maxDiscountAmount?.toString() || '',
        appliesToAllProducts: promotion.appliesToAllProducts === 1,
        billingCycles: [],
        maxUses: promotion.maxUses?.toString() || '',
        maxUsesPerClient: promotion.maxUsesPerClient,
        requireExistingClient: promotion.requireExistingClient === 1,
        newClientsOnly: promotion.newClientsOnly === 1,
        recurringDiscount: promotion.recurringDiscount === 1,
        recurringMonths: promotion.recurringMonths?.toString() || '',
        startsAt: promotion.startsAt
          ? new Date(promotion.startsAt).toISOString().slice(0, 16)
          : '',
        expiresAt: promotion.expiresAt
          ? new Date(promotion.expiresAt).toISOString().slice(0, 16)
          : '',
        isActive: promotion.isActive === 1,
      });
    }
  }, [promotion]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload: any = {
        ...formData,
        maxDiscountAmount: formData.maxDiscountAmount
          ? parseFloat(formData.maxDiscountAmount)
          : undefined,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : undefined,
        recurringMonths: formData.recurringMonths
          ? parseInt(formData.recurringMonths)
          : undefined,
        startsAt: formData.startsAt ? new Date(formData.startsAt).toISOString() : undefined,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : undefined,
      };

      if (promotion) {
        // Update
        await adminApi.put(`/promotions/${promotion.id}`, payload);
      } else {
        // Create
        await adminApi.post('/promotions', payload);
      }

      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save promotion');
      console.error('Error saving promotion:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBillingCycleToggle = (cycle: string) => {
    setFormData((prev) => ({
      ...prev,
      billingCycles: prev.billingCycles.includes(cycle)
        ? prev.billingCycles.filter((c) => c !== cycle)
        : [...prev.billingCycles, cycle],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-slate-800/80 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">
              {promotion ? 'Edit Promotion' : 'Create Promotion'}
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Promotion Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value.toUpperCase() })
                  }
                  disabled={!!promotion}
                  placeholder="SUMMER2024"
                  className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50"
                  required
                />
                <p className="text-xs text-slate-400 mt-1">
                  Uppercase letters, numbers, hyphens, and underscores only
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Summer Sale 2024"
                  className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Get 20% off your first order"
                rows={2}
                className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Discount Settings */}
            <div className="border-t border-slate-700/50 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Discount Settings</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Discount Type *
                  </label>
                  <select
                    value={formData.discountType}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        discountType: e.target.value as 'percentage' | 'fixed',
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Discount Value *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.discountValue}
                    onChange={(e) =>
                      setFormData({ ...formData, discountValue: parseFloat(e.target.value) })
                    }
                    placeholder={formData.discountType === 'percentage' ? '10' : '50.00'}
                    className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {formData.discountType === 'percentage' ? 'Percentage (1-100)' : 'Dollar amount'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Applies To *
                  </label>
                  <select
                    value={formData.appliesTo}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        appliesTo: e.target.value as 'setup' | 'recurring' | 'both',
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="setup">Setup Fee Only</option>
                    <option value="recurring">Recurring Price Only</option>
                    <option value="both">Both</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Minimum Order Value
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.minOrderValue}
                    onChange={(e) =>
                      setFormData({ ...formData, minOrderValue: parseFloat(e.target.value) })
                    }
                    placeholder="0.00"
                    className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Max Discount Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.maxDiscountAmount}
                    onChange={(e) =>
                      setFormData({ ...formData, maxDiscountAmount: e.target.value })
                    }
                    placeholder="No limit"
                    className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Cap for percentage discounts (optional)
                  </p>
                </div>
              </div>
            </div>

            {/* Usage Limits */}
            <div className="border-t border-slate-700/50 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Usage Limits</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Maximum Total Uses
                  </label>
                  <input
                    type="number"
                    value={formData.maxUses}
                    onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                    placeholder="Unlimited"
                    className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Max Uses Per Client *
                  </label>
                  <input
                    type="number"
                    value={formData.maxUsesPerClient}
                    onChange={(e) =>
                      setFormData({ ...formData, maxUsesPerClient: parseInt(e.target.value) })
                    }
                    min="1"
                    className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="border-t border-slate-700/50 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Validity Period</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Start Date/Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.startsAt}
                    onChange={(e) => setFormData({ ...formData, startsAt: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Expiration Date/Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.expiresAt}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Client Restrictions & Recurring */}
            <div className="border-t border-slate-700/50 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Additional Settings</h3>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.requireExistingClient}
                    onChange={(e) =>
                      setFormData({ ...formData, requireExistingClient: e.target.checked })
                    }
                    className="w-4 h-4 bg-slate-700/50 border-gray-600 rounded text-orange-600 focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="ml-2 text-sm text-slate-300">
                    Require Existing Client (only for existing customers)
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.newClientsOnly}
                    onChange={(e) =>
                      setFormData({ ...formData, newClientsOnly: e.target.checked })
                    }
                    className="w-4 h-4 bg-slate-700/50 border-gray-600 rounded text-orange-600 focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="ml-2 text-sm text-slate-300">
                    New Clients Only (only for new signups)
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.recurringDiscount}
                    onChange={(e) =>
                      setFormData({ ...formData, recurringDiscount: e.target.checked })
                    }
                    className="w-4 h-4 bg-slate-700/50 border-gray-600 rounded text-orange-600 focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="ml-2 text-sm text-slate-300">
                    Apply to Recurring Invoices
                  </span>
                </label>

                {formData.recurringDiscount && (
                  <div className="ml-6">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Recurring Months
                    </label>
                    <input
                      type="number"
                      value={formData.recurringMonths}
                      onChange={(e) =>
                        setFormData({ ...formData, recurringMonths: e.target.value })
                      }
                      placeholder="Lifetime (leave empty)"
                      className="w-48 px-3 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                )}

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 bg-slate-700/50 border-gray-600 rounded text-orange-600 focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="ml-2 text-sm text-slate-300">Active (enabled)</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-slate-700/50">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : promotion ? 'Update Promotion' : 'Create Promotion'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
